'use client';

import { checkoutPageConstant } from '@next-app/constants/CheckoutConstants';
import {
  selectInitResponse,
  selectIsGuestUser,
  selectIsLoggedIn,
  selectSessionInfo,
  selectUserProfile,
} from '@next-app/lib/features/InitSelectors';
import {
  selectCommerceItems,
  selectIsCheckoutDetailsLoaded,
  selectOrderInfo,
} from '@next-app/lib/features/checkout/CheckoutSelector';
import { getCartItemsForDTMEvent } from '@next-app/utils/CheckoutHelper';
import { DynamicDTMEvent } from '@next-app/utils/dtm-event';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

const CheckoutPageLoadDTMEvent = () => {
  const isLoadCheckoutLoaded = useSelector(selectIsCheckoutDetailsLoaded);
  const sessionInfo = useSelector(selectSessionInfo);
  const userProfile = useSelector(selectUserProfile);
  const initResp = useSelector(selectInitResponse);
  const orderInfo = useSelector(selectOrderInfo);
  const isLoggedInUser = useSelector(selectIsLoggedIn);
  const isGuestUser = useSelector(selectIsGuestUser);
  const isGSAUser = useSelector(selectIsGuestUser);
  const commerceItems = useSelector(selectCommerceItems);

  const [isSendDTMLoaded, setIsSendDTMLoaded] = useState(false);

  const isEventFired = useRef(false);

  useEffect(() => {
    let loadDtmEventTimeout: ReturnType<typeof setTimeout>;
    const checkIfDTMEventIsLoaded = () => {
      if ((window as any).sendDTMEvent) {
        setIsSendDTMLoaded(true);
        clearInterval(loadDtmEventTimeout);
      }
    };

    loadDtmEventTimeout = setInterval(checkIfDTMEventIsLoaded, 250);

    return () => {
      clearInterval(loadDtmEventTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isLoadCheckoutLoaded && isSendDTMLoaded && !isEventFired.current) {
      //create the body DTM body here.
      const eventData = {
        cart: {
          items: getCartItemsForDTMEvent(commerceItems),
        },
        cartCount: orderInfo?.totalCommerceItemCount,
        cartRevenue: orderInfo?.priceInfo?.amount,
        checkoutShopper: isLoggedInUser
          ? 'signedIn'
          : isGuestUser
            ? 'guest'
            : '',
        email: userProfile?.email || '',
        homeAddress: userProfile?.address,
        isFurniture: sessionInfo?.furniture,
        isGSAUser: isGSAUser,
        llmAPIVersion: initResp?.internalAPI,
        md5Email: userProfile?.md5Email || '',
        orderId: orderInfo?.id,
        orgId: '',
        orgName: '',
        pageName: checkoutPageConstant.PAGE_TITLE,
        pageSection: 'checkout',
        pageTitle: checkoutPageConstant.PAGE_TITLE,
        pageType: 'checkout',
        pageUrl: window.location.pathname,
        profileId: userProfile?.id,
        recognized: userProfile?.loginStatus === 'recognized' || false,
        rewardsUserStatus: sessionInfo?.rewardsCustomer?.tierInfo?.currentTier
          ? sessionInfo?.rewardsCustomer?.tierInfo?.currentTier
          : sessionInfo?.rewardsCustomer?.isRewardsMember &&
              userProfile?.securityStatus >= 4
            ? 'Signed In'
            : 'Guest',
        ...(userProfile.securityStatus && {
          securityStatus: userProfile.securityStatus,
        }),
        storeSelected:
          Object.keys(userProfile?.selectedStore).length > 0
            ? `${userProfile?.selectedStore?.city}, ${userProfile?.selectedStore?.state}`
            : '',
        userShopFor: userProfile?.userShopFor || '',
      };

      // trigger the DTM event
      DynamicDTMEvent({ event: 'pageLoadData', detail: eventData });

      // once DTM event is triggered
      isEventFired.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadCheckoutLoaded, isSendDTMLoaded]);

  return null;
};

export default CheckoutPageLoadDTMEvent;
